import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { api } from "../../lib/api";
import { Icon } from "./Icon";
import { ProductThumb } from "./ProductThumb";

export type SearchProduct = {
  sku: string;
  name: string;
  image_url: string | null;
  category: string | null;
  quantity: number;
};

export function ProductSearch({
  onPick,
  selectedSku,
  placeholder = "Cerca per nome o SKU…",
}: {
  onPick: (p: SearchProduct) => void;
  selectedSku?: string;
  placeholder?: string;
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const trimmedSearch = searchTerm.trim();

  const { data: searchRes, isFetching: searching } = useQuery<{ items: SearchProduct[] }>({
    queryKey: ["product-search", trimmedSearch],
    queryFn: async () =>
      (await api.get("/api/products", { params: { search: trimmedSearch, page_size: 20 } })).data,
    enabled: trimmedSearch.length >= 2,
    staleTime: 10_000,
  });
  const searchItems = trimmedSearch.length >= 2 ? searchRes?.items ?? [] : [];

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          background: "var(--tcg-card)",
          border: "1.5px solid var(--tcg-border)",
          borderRadius: 12,
          padding: "10px 13px",
        }}
      >
        <Icon name="search" size={14} />
        <input
          style={{
            flex: 1,
            minWidth: 0,
            border: "none",
            outline: "none",
            fontSize: 13,
            fontWeight: 600,
            background: "transparent",
          }}
          placeholder={placeholder}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          autoComplete="off"
          spellCheck={false}
        />
        {searching && <span style={{ fontSize: 10, color: "var(--tcg-muted)" }}>···</span>}
        {searchTerm && (
          <button
            type="button"
            onClick={() => setSearchTerm("")}
            aria-label="pulisci ricerca"
            style={{
              border: "none",
              background: "transparent",
              color: "var(--tcg-muted)",
              cursor: "pointer",
              display: "flex",
              padding: 0,
            }}
          >
            <Icon name="x" size={14} />
          </button>
        )}
      </div>

      {trimmedSearch.length >= 2 && (
        <div
          style={{
            marginTop: 8,
            background: "var(--tcg-card)",
            border: "1px solid var(--tcg-border)",
            borderRadius: 12,
            overflow: "hidden",
          }}
        >
          {searchItems.length === 0 && !searching && (
            <div style={{ padding: 14, fontSize: 12, color: "var(--tcg-muted)", textAlign: "center" }}>
              Nessun prodotto trovato.
            </div>
          )}
          {searchItems.map((p, i, arr) => (
            <button
              key={p.sku}
              type="button"
              onClick={() => {
                onPick(p);
                setSearchTerm("");
              }}
              style={{
                display: "flex",
                gap: 10,
                alignItems: "center",
                width: "100%",
                textAlign: "left",
                padding: 10,
                background: selectedSku === p.sku ? "var(--tcg-bg)" : "transparent",
                border: "none",
                borderBottom: i === arr.length - 1 ? "none" : "1px solid var(--tcg-border)",
                cursor: "pointer",
                fontFamily: "inherit",
                color: "inherit",
              }}
            >
              <ProductThumb imageUrl={p.image_url} sku={p.sku} category={p.category} size="xs" radius={9} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {p.name}
                </div>
                <div
                  className="tcg-mono"
                  style={{ fontSize: 10, color: "var(--tcg-muted)", fontWeight: 600, marginTop: 1 }}
                >
                  {p.sku} · stock {p.quantity}
                </div>
              </div>
              <Icon name="plus" size={14} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
